import { cloneElement, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const Context = createContext(null);
export function AccountScopeProvider({ accountId, accounts, children }) {
  const actorJSON = localStorage.getItem('user') || '{}';
  const actor = useMemo(() => JSON.parse(actorJSON), [actorJSON]);
  const id = Number(accountId || actor.id);
  const currentId = useRef(id);
  currentId.current = id;
  const [revision, setRevision] = useState(0);
  const account = useMemo(() => accounts.find(item => Number(item.id) === id) ||
    (id === Number(actor.id) ? actor : { id, name: 'Cargando cuenta…', role: 'usuario' }), [accounts, actor, id]);
  const refreshAccountScope = useCallback(() => {
    setRevision(value => value + 1);
    window.dispatchEvent(new Event('maccez:profile-updated'));
  }, []);
  const value = useMemo(() => ({ account, actor, id, revision, currentId,
    isOwn: id === Number(actor.id), refreshAccountScope
  }), [account, actor, id, revision, refreshAccountScope]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAccountScope() {
  const scope = useContext(Context);
  if (!scope) throw new Error('AccountScopeProvider no disponible');
  const requests = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const pending = requests.current;
    return () => {
      mounted.current = false;
      for (const controller of pending) controller.abort();
      pending.clear();
    };
  }, [scope.id]);
  const scopedFetch = useCallback(async (url, options = {}) => {
    const current = () => mounted.current && scope.currentId.current === scope.id;
    const aborted = () => new DOMException('La cuenta seleccionada cambió', 'AbortError');
    if (!current()) throw aborted();
    const controller = new AbortController();
    requests.current.add(controller);
    const stop = () => controller.abort();
    options.signal?.addEventListener('abort', stop, { once: true });
    if (options.signal?.aborted) controller.abort();
    const headers = new Headers(options.headers);
    headers.set('X-Monitor-User-Id', String(scope.id));
    try {
      const response = await window.fetch(url, { ...options, headers, signal: controller.signal });
      if (!current()) throw aborted();
      // Also guard delayed JSON/body decoding after changing accounts.
      for (const method of ['json', 'text', 'blob', 'arrayBuffer']) {
        const original = response[method].bind(response);
        response[method] = async (...args) => {
          const body = await original(...args);
          if (!current()) throw aborted();
          return body;
        };
      }
      return response;
    } finally {
      requests.current.delete(controller);
      options.signal?.removeEventListener('abort', stop);
    }
  }, [scope.id, scope.currentId]);
  return { ...scope, fetch: scopedFetch };
}

export function AccountScopePage({ children }) {
  const { id, revision } = useContext(Context);
  return cloneElement(children, { key: `${id}:${revision}` });
}
